import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Save, Send, Download, FileText, Plus, Trash2,
  CheckCircle, Users, ClipboardList, Sparkles, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MeetingMinutesEditor() {
  const { minutesId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [minutes, setMinutes] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [users, setUsers] = useState([]);
  const [orgReports, setOrgReports] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, [minutesId]);

  const fetchData = async () => {
    try {
      const minutesRes = await axios.get(`${API}/meeting-minutes/${minutesId}`);
      setMinutes(minutesRes.data);
      
      // Fetch related data
      const [meetingRes, usersRes, orgsRes] = await Promise.all([
        axios.get(`${API}/scheduled-meetings/${minutesRes.data.scheduled_meeting_id}`),
        axios.get(`${API}/users`),
        axios.get(`${API}/organizations`)
      ]);
      
      setMeeting(meetingRes.data);
      setUsers(usersRes.data.filter(u => u.status === 'active'));
      setOrganizations(orgsRes.data);
      
      // Fetch org reports for this meeting
      const reportsRes = await axios.get(`${API}/org-reports?meeting_id=${minutesRes.data.scheduled_meeting_id}`);
      setOrgReports(reportsRes.data.filter(r => r.status === 'submitted'));
      
    } catch (error) {
      toast.error('Failed to load minutes data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (newStatus = null) => {
    setSaving(true);
    try {
      const updates = {
        ...minutes,
        status: newStatus || minutes.status
      };
      delete updates.id;
      delete updates.scheduled_meeting_id;
      delete updates.created_at;
      
      await axios.put(`${API}/meeting-minutes/${minutesId}`, updates);
      toast.success(newStatus ? `Minutes ${newStatus === 'pending_approval' ? 'sent for approval' : 'saved'}` : 'Minutes saved');
      fetchData();
    } catch (error) {
      toast.error('Failed to save minutes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${API}/meeting-minutes/${minutesId}/approve`);
      toast.success('Minutes approved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve minutes');
    }
  };

  const handleImportTranscript = () => {
    if (!transcriptText.trim()) {
      toast.error('Please paste a transcript');
      return;
    }
    
    setMinutes({
      ...minutes,
      ai_transcription: transcriptText
    });
    setShowImportDialog(false);
    toast.success('Transcript imported! Now use "Parse Transcript" to extract content.');
  };

  const parseTranscriptToMinutes = () => {
    if (!minutes.ai_transcription) {
      toast.error('No transcript to parse. Import one first.');
      return;
    }
    
    // Simple parsing - in production this could use AI
    const transcript = minutes.ai_transcription;
    const lines = transcript.split('\n').filter(l => l.trim());
    
    // Extract potential decisions (lines with "decided", "agreed", "approved")
    const decisions = lines
      .filter(l => /\b(decided|agreed|approved|resolved|confirmed)\b/i.test(l))
      .map(l => l.trim())
      .slice(0, 10);
    
    // Extract potential action items (lines with "action:", "todo:", "will", "to do")
    const actions = lines
      .filter(l => /\b(action:|todo:|will\s+\w+|to\s+do|assigned\s+to)\b/i.test(l))
      .map(l => l.trim())
      .slice(0, 10);
    
    // Auto-populate reports summary from submitted org reports
    const reportsSummary = orgReports.map(report => {
      const org = organizations.find(o => o.id === report.organization_id);
      return {
        org_name: org?.name || 'Unknown',
        rag: report.overall_rag_status || 'green',
        key_points: report.tasks_completed?.substring(0, 100) || 'Report submitted'
      };
    });
    
    setMinutes({
      ...minutes,
      key_decisions: decisions.length > 0 ? decisions : minutes.key_decisions,
      reports_summary: reportsSummary.length > 0 ? reportsSummary : minutes.reports_summary
    });
    
    toast.success(`Parsed transcript: ${decisions.length} decisions found, ${reportsSummary.length} reports populated`);
  };

  const addKeyDecision = () => {
    setMinutes({
      ...minutes,
      key_decisions: [...(minutes.key_decisions || []), '']
    });
  };

  const updateKeyDecision = (index, value) => {
    const updated = [...(minutes.key_decisions || [])];
    updated[index] = value;
    setMinutes({ ...minutes, key_decisions: updated });
  };

  const removeKeyDecision = (index) => {
    setMinutes({
      ...minutes,
      key_decisions: (minutes.key_decisions || []).filter((_, i) => i !== index)
    });
  };

  const addDiscussion = () => {
    setMinutes({
      ...minutes,
      discussion_summaries: [...(minutes.discussion_summaries || []), {
        title: '',
        summary: '',
        decisions: [],
        actions: []
      }]
    });
  };

  const updateDiscussion = (index, field, value) => {
    const updated = [...(minutes.discussion_summaries || [])];
    updated[index][field] = value;
    setMinutes({ ...minutes, discussion_summaries: updated });
  };

  const removeDiscussion = (index) => {
    setMinutes({
      ...minutes,
      discussion_summaries: (minutes.discussion_summaries || []).filter((_, i) => i !== index)
    });
  };

  const addAOB = () => {
    setMinutes({
      ...minutes,
      aob_items: [...(minutes.aob_items || []), '']
    });
  };

  const updateAOB = (index, value) => {
    const updated = [...(minutes.aob_items || [])];
    updated[index] = value;
    setMinutes({ ...minutes, aob_items: updated });
  };

  const removeAOB = (index) => {
    setMinutes({
      ...minutes,
      aob_items: (minutes.aob_items || []).filter((_, i) => i !== index)
    });
  };

  const addActionClosed = () => {
    setMinutes({
      ...minutes,
      actions_closed: [...(minutes.actions_closed || []), { action_ref: '', outcome: '' }]
    });
  };

  const updateActionClosed = (index, field, value) => {
    const updated = [...(minutes.actions_closed || [])];
    updated[index][field] = value;
    setMinutes({ ...minutes, actions_closed: updated });
  };

  const removeActionClosed = (index) => {
    setMinutes({
      ...minutes,
      actions_closed: (minutes.actions_closed || []).filter((_, i) => i !== index)
    });
  };

  const downloadPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/meeting-minutes/${minutesId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HLRA_Minutes_${minutes.meeting_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
  }

  if (!minutes) {
    return <div className="text-center py-12">Minutes not found</div>;
  }

  return (
    <div className="space-y-6 fade-in max-w-5xl mx-auto" data-testid="minutes-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex gap-2">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            minutes.status === 'approved' ? 'bg-green-100 text-green-700' :
            minutes.status === 'pending_approval' ? 'bg-orange-100 text-orange-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {minutes.status === 'approved' ? 'Approved' :
             minutes.status === 'pending_approval' ? 'Pending Approval' : 'Draft'}
          </span>
        </div>
      </div>

      {/* Title */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Meeting Minutes</h1>
        <p className="text-lg text-gray-600">{minutes.meeting_title || meeting?.title}</p>
        <p className="text-sm text-gray-500">{minutes.meeting_date}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-center gap-3 flex-wrap">
        <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="import-transcript-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Import Tactiq Transcript
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Import AI Transcript</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Paste the transcript from Tactiq AI or any other meeting transcription tool.
                After importing, use "Parse Transcript" to extract key decisions and action items.
              </p>
              <Textarea
                value={transcriptText}
                onChange={(e) => setTranscriptText(e.target.value)}
                placeholder="Paste your meeting transcript here..."
                rows={12}
                className="font-mono text-sm"
              />
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancel</Button>
                <Button onClick={handleImportTranscript} className="bg-gradient-to-r from-blue-500 to-green-500">
                  Import Transcript
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
        
        {minutes.ai_transcription && (
          <Button variant="outline" onClick={parseTranscriptToMinutes} data-testid="parse-transcript-btn">
            <Sparkles className="w-4 h-4 mr-2" />
            Parse Transcript
          </Button>
        )}
        
        <Button variant="outline" onClick={downloadPDF}>
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="decisions">Decisions</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="discussions">Discussions</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Meeting Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Meeting Title</Label>
                  <Input
                    value={minutes.meeting_title || ''}
                    onChange={(e) => setMinutes({...minutes, meeting_title: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={minutes.meeting_date || ''}
                    onChange={(e) => setMinutes({...minutes, meeting_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Chair</Label>
                <Input
                  value={minutes.chair_name || ''}
                  onChange={(e) => setMinutes({...minutes, chair_name: e.target.value})}
                  placeholder="Name of meeting chair"
                />
              </div>
              <div className="space-y-2">
                <Label>Attendees (comma separated)</Label>
                <Textarea
                  value={(minutes.attendees_list || []).join(', ')}
                  onChange={(e) => setMinutes({
                    ...minutes, 
                    attendees_list: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  placeholder="John Smith, Jane Doe, Bob Wilson"
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Apologies (comma separated)</Label>
                <Textarea
                  value={(minutes.apologies_list || []).join(', ')}
                  onChange={(e) => setMinutes({
                    ...minutes, 
                    apologies_list: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  placeholder="Names of those who sent apologies"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Next Meeting Date</Label>
                  <Input
                    type="date"
                    value={minutes.next_meeting_date || ''}
                    onChange={(e) => setMinutes({...minutes, next_meeting_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Next Meeting Location</Label>
                  <Input
                    value={minutes.next_meeting_location || ''}
                    onChange={(e) => setMinutes({...minutes, next_meeting_location: e.target.value})}
                    placeholder="Google Meet / Village Hall"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Key Decisions Tab */}
        <TabsContent value="decisions" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Key Decisions</CardTitle>
                  <CardDescription>Important decisions made during the meeting</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={addKeyDecision}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Decision
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(minutes.key_decisions || []).length === 0 ? (
                <p className="text-center text-gray-500 py-4">No key decisions recorded</p>
              ) : (
                (minutes.key_decisions || []).map((decision, index) => (
                  <div key={index} className="flex gap-2">
                    <span className="text-gray-400 mt-2">{index + 1}.</span>
                    <Input
                      value={decision}
                      onChange={(e) => updateKeyDecision(index, e.target.value)}
                      placeholder="Enter decision..."
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeKeyDecision(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Summary Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Organization Reports Summary</CardTitle>
              <CardDescription>Summary of partner organization reports presented</CardDescription>
            </CardHeader>
            <CardContent>
              {orgReports.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No submitted reports for this meeting</p>
              ) : (
                <div className="space-y-4">
                  {orgReports.map((report, index) => {
                    const org = organizations.find(o => o.id === report.organization_id);
                    const existingSummary = (minutes.reports_summary || []).find(s => s.org_name === org?.name);
                    
                    return (
                      <div key={report.id} className="p-4 border rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`w-3 h-3 rounded-full ${
                            report.overall_rag_status === 'green' ? 'bg-green-500' :
                            report.overall_rag_status === 'amber' ? 'bg-amber-500' : 'bg-red-500'
                          }`} />
                          <h4 className="font-semibold">{org?.name || 'Unknown'}</h4>
                          <span className="text-xs text-gray-500">({report.overall_rag_status?.toUpperCase()})</span>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <Label className="text-xs">Key Points for Minutes</Label>
                            <Textarea
                              value={existingSummary?.key_points || report.tasks_completed?.substring(0, 200) || ''}
                              onChange={(e) => {
                                const updated = [...(minutes.reports_summary || [])];
                                const idx = updated.findIndex(s => s.org_name === org?.name);
                                if (idx >= 0) {
                                  updated[idx].key_points = e.target.value;
                                } else {
                                  updated.push({
                                    org_name: org?.name,
                                    rag: report.overall_rag_status,
                                    key_points: e.target.value
                                  });
                                }
                                setMinutes({ ...minutes, reports_summary: updated });
                              }}
                              rows={2}
                              placeholder="Summarize key points from this report..."
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Discussions Tab */}
        <TabsContent value="discussions" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Discussion Items</CardTitle>
                  <CardDescription>Summary of topics discussed</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={addDiscussion}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Discussion
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {(minutes.discussion_summaries || []).length === 0 ? (
                <p className="text-center text-gray-500 py-4">No discussion items recorded</p>
              ) : (
                (minutes.discussion_summaries || []).map((disc, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 space-y-3">
                        <div>
                          <Label>Topic Title</Label>
                          <Input
                            value={disc.title || ''}
                            onChange={(e) => updateDiscussion(index, 'title', e.target.value)}
                            placeholder="Discussion topic..."
                          />
                        </div>
                        <div>
                          <Label>Summary</Label>
                          <Textarea
                            value={disc.summary || ''}
                            onChange={(e) => updateDiscussion(index, 'summary', e.target.value)}
                            placeholder="Summary of discussion..."
                            rows={3}
                          />
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 ml-2" onClick={() => removeDiscussion(index)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* AOB */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Any Other Business</CardTitle>
                </div>
                <Button variant="outline" size="sm" onClick={addAOB}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {(minutes.aob_items || []).length === 0 ? (
                <p className="text-center text-gray-500 py-4">No AOB items</p>
              ) : (
                (minutes.aob_items || []).map((item, index) => (
                  <div key={index} className="flex gap-2">
                    <span className="text-gray-400 mt-2">•</span>
                    <Input
                      value={item}
                      onChange={(e) => updateAOB(index, e.target.value)}
                      placeholder="AOB item..."
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeAOB(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Actions Tab */}
        <TabsContent value="actions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Actions Closed</CardTitle>
              <CardDescription>Actions completed or closed at this meeting</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" size="sm" onClick={addActionClosed}>
                <Plus className="w-4 h-4 mr-1" />
                Add Closed Action
              </Button>
              {(minutes.actions_closed || []).length === 0 ? (
                <p className="text-center text-gray-500 py-4">No actions closed</p>
              ) : (
                (minutes.actions_closed || []).map((action, index) => (
                  <div key={index} className="flex gap-2 items-start">
                    <Input
                      value={action.action_ref || ''}
                      onChange={(e) => updateActionClosed(index, 'action_ref', e.target.value)}
                      placeholder="ACT-0001"
                      className="w-32"
                    />
                    <Input
                      value={action.outcome || ''}
                      onChange={(e) => updateActionClosed(index, 'outcome', e.target.value)}
                      placeholder="Outcome / reason for closing..."
                      className="flex-1"
                    />
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => removeActionClosed(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Transcript Preview */}
          {minutes.ai_transcription && (
            <Card>
              <CardHeader>
                <CardTitle>Imported Transcript</CardTitle>
                <CardDescription>Raw transcript from Tactiq AI</CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto max-h-64 whitespace-pre-wrap">
                  {minutes.ai_transcription}
                </pre>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Save Buttons */}
      <div className="flex justify-center gap-3 pb-8">
        <Button variant="outline" onClick={() => handleSave()} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          Save Draft
        </Button>
        {minutes.status === 'draft' && (
          <Button 
            variant="outline"
            className="text-orange-600 border-orange-300 hover:bg-orange-50"
            onClick={() => handleSave('pending_approval')} 
            disabled={saving}
          >
            <Send className="w-4 h-4 mr-2" />
            Submit for Approval
          </Button>
        )}
        {minutes.status === 'pending_approval' && isAdmin && (
          <Button 
            onClick={handleApprove}
            className="bg-gradient-to-r from-blue-500 to-green-500"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Approve Minutes
          </Button>
        )}
      </div>
    </div>
  );
}
